// PWA-обвязка vista-web: регистрация service worker'а, баннер обновления и
// установка на домашний экран.
//
// Почему это вообще важно для vista: контрибьютор снимает с телефона, часто на
// улице. Приложение с иконкой на домашнем экране открывается в один тап и без
// адресной строки — экран съёмки получает лишние ~60px по высоте и не рискует
// закрыться случайным свайпом по вкладкам.
import { useEffect, useState } from 'preact/hooks';

/* ─────────────────────────── крошечный внешний стор ───────────────────────── */

function store<T>(initial: T) {
  let value = initial;
  const subs = new Set<() => void>();
  return {
    get: () => value,
    set(next: T) {
      if (next === value) return;
      value = next;
      subs.forEach((f) => f());
    },
    use(): T {
      const [, force] = useState(0);
      useEffect(() => {
        const f = () => force((n) => n + 1);
        subs.add(f);
        return () => void subs.delete(f);
      }, []);
      return value;
    },
  };
}

/* ────────────────────────────── обновление сборки ─────────────────────────── */

/**
 * Новая сборка перехватила управление под уже открытой страницей — значит
 * текущая вкладка выполняет устаревший бандл. Показываем баннер и ждём тапа.
 *
 * Автоматическую перезагрузку не делаем НИКОГДА: перезагрузка посреди записи
 * уничтожит дубль. Человек обновится сам, когда не снимает.
 */
const updateStore = store(false);
export const useUpdateAvailable = (): boolean => updateStore.use();

export function registerSW(): void {
  if (!import.meta.env.PROD) return; // дев-сервер не должен перекрываться кешем
  if (!('serviceWorker' in navigator)) return;

  // Защита от ПЕРВОЙ установки: clients.claim() у нового SW стреляет
  // controllerchange, хотя никакого «обновления» не было. Без этой проверки
  // новый человек видел «доступна новая версия» через секунду после первого
  // захода (грабли kids, 20.07).
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) updateStore.set(true);
    hadController = true;
  });

  addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              sw.postMessage('skip-waiting');
            }
          });
        });
        // PWA, поднятая из переключателя приложений, не выполняет `load`
        // заново — проверяем обновление при каждом возврате на передний план.
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) void reg.update().catch(() => {});
        });
      })
      .catch(() => {
        /* SW — улучшение, не необходимость: молча живём без него */
      });
  });
}

/* ─────────────────────── авто-обновление по версии сборки ─────────────────── */

/**
 * Опрос version.json — второй, ГЛАВНЫЙ канал обновления. Схема с одним лишь
 * service worker'ом (как в kids) обновляет только страницы, где SW уже
 * зарегистрирован; бандл, выкаченный ДО появления SW, или iOS-PWA со спящим
 * процессом так и остаются на старой сборке (Tim, 11.08: «обновлял и
 * перезаходил — всё ещё старый интерфейс»). Сравнение «мой вшитый тег против
 * version.json с сервера» работает из любого состояния.
 *
 * Поведение: safeToReload() === true → тихая перезагрузка (человек просто
 * видит свежую версию); false (идёт запись/загрузка) → баннер, как раньше.
 * Против reload-петли (сервер раздаёт старый version.json из-за кеша) — один
 * авто-релоад на тег за сессию.
 */
const BUILD_TAG: string = (import.meta.env.VITE_BUILD_TAG as string | undefined) ?? 'dev';
const VER_RELOADED_KEY = 'vista.ver.reloaded';

export function startVersionWatch(safeToReload: () => boolean): void {
  if (!import.meta.env.PROD || BUILD_TAG === 'dev') return;

  let checking = false;
  const check = async () => {
    if (checking) return;
    checking = true;
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const { tag } = (await res.json()) as { tag?: string };
      if (!tag || tag === BUILD_TAG) return;
      if (safeToReload() && sessionStorage.getItem(VER_RELOADED_KEY) !== tag) {
        sessionStorage.setItem(VER_RELOADED_KEY, tag);
        location.reload();
        return;
      }
      updateStore.set(true);
    } catch {
      /* офлайн/сбой сети — проверимся в следующий раз */
    } finally {
      checking = false;
    }
  };

  // Возврат PWA на передний план — главный момент «человек перезашёл и ждёт
  // свежее»; плюс редкий фоновый пульс для вкладок, живущих часами.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void check();
  });
  setInterval(() => void check(), 5 * 60 * 1000);
  void check();
}

/* ──────────────────────────── установка на телефон ────────────────────────── */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const promptStore = store<InstallPromptEvent | null>(null);

/** Уже открыто как установленное приложение (или добавлено на экран в iOS). */
export function isStandalone(): boolean {
  return (
    matchMedia('(display-mode: standalone)').matches ||
    // iOS до сих пор не поддерживает display-mode для «на экран Домой»
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export const isIOS = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ представляется маком; отличаем по тач-экрану
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** Chrome/Android дают событие заранее — перехватываем и показываем СВОЮ кнопку. */
export function captureInstallPrompt(): void {
  addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault(); // иначе браузер покажет собственную плашку, когда захочет
    promptStore.set(e as InstallPromptEvent);
  });
  addEventListener('appinstalled', () => promptStore.set(null));
}

export type InstallMode = 'installed' | 'prompt' | 'ios-manual' | 'unavailable';

/**
 * Что показывать человеку прямо сейчас:
 *   installed    — уже стоит, предлагать нечего;
 *   prompt       — есть системное приглашение, хватит одной кнопки;
 *   ios-manual   — Safari: API установки нет вовсе, только инструкция руками;
 *   unavailable  — десктоп или браузер без поддержки, не мозолим глаза.
 */
export function useInstallMode(): InstallMode {
  const prompt = promptStore.use();
  const [standalone, setStandalone] = useState(isStandalone);

  useEffect(() => {
    const mq = matchMedia('(display-mode: standalone)');
    const on = () => setStandalone(isStandalone());
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  if (standalone) return 'installed';
  if (prompt) return 'prompt';
  if (isIOS()) return 'ios-manual';
  return 'unavailable';
}

/** Показать системное окно установки. true — человек согласился. */
export async function runInstallPrompt(): Promise<boolean> {
  const e = promptStore.get();
  if (!e) return false;
  await e.prompt();
  const { outcome } = await e.userChoice;
  // Событие одноразовое: второй prompt() на том же объекте бросит исключение.
  promptStore.set(null);
  return outcome === 'accepted';
}
